import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api from '../../../../api/client';
import useResolvedCompanyId from '../../../../hooks/useResolvedCompanyId';
import {
  hasProviderConfigured,
  persistWhatsAppCompanyId,
} from './whatsappTemplateShared';

const WhatsAppSettingsContext = createContext(null);

function mergeSettingsWithDraft(serverSettings, draft) {
  if (!draft) return serverSettings || {};
  const provider = draft.active_provider || serverSettings?.active_provider;
  const merged = {
    ...(serverSettings || {}),
    ...draft,
    active_provider: provider,
    event_toggles: {
      ...(serverSettings?.event_toggles || {}),
      ...(draft.event_toggles || {}),
    },
    providers: {
      ...(serverSettings?.providers || {}),
      ...(draft.providers || {}),
    },
  };
  if (provider) {
    merged.providers = {
      ...merged.providers,
      [provider]: {
        ...(serverSettings?.providers?.[provider] || {}),
        ...(draft.providers?.[provider] || {}),
      },
    };
  }
  return merged;
}

export function WhatsAppSettingsProvider({ children }) {
  const { companyId, loading: companyLoading } = useResolvedCompanyId();
  const [serverSettings, setServerSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadSettings = useCallback(async () => {
    if (!companyId) {
      setServerSettings(null);
      setLoadingSettings(false);
      return null;
    }
    setLoadingSettings(true);
    setLoadError('');
    try {
      const { data } = await api.get(`/companies/${companyId}/whatsapp-settings/`);
      setServerSettings(data);
      setDraft((prev) => prev || data);
      persistWhatsAppCompanyId(companyId);
      return data;
    } catch (err) {
      setLoadError(
        err.response?.data?.error || err.message || 'Failed to load WhatsApp settings'
      );
      return null;
    } finally {
      setLoadingSettings(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyLoading && companyId) {
      loadSettings();
    }
  }, [companyLoading, companyId, loadSettings]);

  useEffect(() => {
    const onUpdated = () => loadSettings();
    window.addEventListener('whatsapp-settings-updated', onUpdated);
    return () => window.removeEventListener('whatsapp-settings-updated', onUpdated);
  }, [loadSettings]);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => {
      const base = { ...(prev || serverSettings || {}), ...patch };
      if (patch.providers) {
        base.providers = {
          ...(prev?.providers || serverSettings?.providers || {}),
          ...patch.providers,
        };
      }
      if (patch.event_toggles) {
        base.event_toggles = {
          ...(prev?.event_toggles || serverSettings?.event_toggles || {}),
          ...patch.event_toggles,
        };
      }
      return base;
    });
  }, [serverSettings]);

  const effectiveSettings = useMemo(
    () => mergeSettingsWithDraft(serverSettings, draft),
    [serverSettings, draft]
  );

  const connectionReady = useMemo(
    () => hasProviderConfigured(effectiveSettings) && !serverSettings?.feature_disabled,
    [effectiveSettings, serverSettings]
  );

  const saveConnection = useCallback(
    async (payload) => {
      if (!companyId) return { ok: false, error: 'No company' };
      try {
        const { data } = await api.put(
          `/companies/${companyId}/whatsapp-settings/`,
          payload
        );
        setServerSettings(data);
        setDraft(data);
        persistWhatsAppCompanyId(companyId);
        window.dispatchEvent(new CustomEvent('whatsapp-settings-updated'));
        return { ok: true, data };
      } catch (err) {
        const error =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Save failed';
        return { ok: false, error };
      }
    },
    [companyId]
  );

  const value = useMemo(
    () => ({
      companyId,
      companyLoading: companyLoading || loadingSettings,
      serverSettings,
      draft,
      effectiveSettings,
      connectionReady,
      loadError,
      featureDisabled: Boolean(serverSettings?.feature_disabled),
      updateDraft,
      loadSettings,
      saveConnection,
      setDraft,
      setServerSettings,
    }),
    [
      companyId,
      companyLoading,
      loadingSettings,
      serverSettings,
      draft,
      effectiveSettings,
      connectionReady,
      loadError,
      updateDraft,
      loadSettings,
      saveConnection,
    ]
  );

  return (
    <WhatsAppSettingsContext.Provider value={value}>
      {children}
    </WhatsAppSettingsContext.Provider>
  );
}

export function useWhatsAppSettingsContext() {
  const ctx = useContext(WhatsAppSettingsContext);
  if (!ctx) {
    throw new Error('useWhatsAppSettingsContext must be used within WhatsAppSettingsProvider');
  }
  return ctx;
}

export default WhatsAppSettingsContext;
